<script lang="ts">
  import { STATIC_URL } from '$lib/constants';
  import { searchIndex } from '$lib/utils';
  import { createEventDispatcher, onMount } from 'svelte';
  import { fade } from 'svelte/transition';

  import EmptyState from '$components/EmptyState.svelte';
  import Button from '$mds/Buttons/Button';
  import IconButton from '$mds/Buttons/IconButton/IconButton.svelte';

  import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
  import { MetafyPartnerEnum } from '$lib/types/api/graphql';
  import ExpertCardSquare from '$components/cards/ExpertCardSquare.svelte';
  import GameCard from '$components/cards/GameCard.svelte';
  import GroupCard from './GroupCard.svelte';
  import SessionCard from './SessionCard.svelte';
  import GuideCard from './GuideCard.svelte';
  import EventCard from './EventCard.svelte';

  const dispatch = createEventDispatcher();

  export let selectedKey = '';
  export let query = '';
  export let currentGame = null;

  const indices = {
    games: { indexName: 'games', params: { hitsPerPage: 12, attributesToHighlight: ['title.en'] } },
    accounts: {
      indexName: 'accounts',
      params: {
        hitsPerPage: 15,
        filter: '(is_core = true OR is_pro = true)',
        attributesToHighlight: ['name', 'game_titles']
      }
    },
    groups: {
      indexName: 'groups',
      params: { hitsPerPage: 15, sort: ['member_count:desc'], attributesToHighlight: ['title', 'description'] }
    },
    tags: { indexName: 'unified_tags', params: { hitsPerPage: 18, attributesToHighlight: ['name'] } },
    product_guides: {
      indexName: 'product_guides',
      params: { hitsPerPage: 12, attributesToHighlight: ['title', 'seller_name'] }
    },
    product_coaching: {
      indexName: 'product_coaching',
      params: { hitsPerPage: 12, attributesToHighlight: ['name', 'seller_name', 'description'] }
    },
    product_events: {
      indexName: 'product_events',
      params: {
        hitsPerPage: 12,
        filter: '(offer_replay_purchase = true OR is_complete = false)',
        attributesToHighlight: ['name', 'seller_name', 'description']
      }
    }
  };

  const titleIndex = {
    accounts: 'users',
    product_guides: 'guides',
    product_coaching: 'sessions',
    product_events: 'events',
    groups: 'communities'
  };

  let hasNextPage = true;
  let nextPage = 0;
  let isFetching = true;
  let isFetchingNextPage = false;

  let results = { hits: [], indexName: '', totalHits: 0 };

  $: indexFilterMap = {
    accounts: `games.id = ${currentGame?.id}`,
    groups: `game_id = ${currentGame?.id}`,
    games: [],
    unified_tags: `games.id = ${currentGame?.id}`,
    product_guides: `game_id = ${currentGame?.id}`,
    product_coaching: `game_id = ${currentGame?.id}`,
    product_events: `game_id = ${currentGame?.id}`
  };

  async function doSearch(page = 0) {
    if (!selectedKey) return;

    try {
      if (page === 0) {
        isFetching = true;
      } else {
        isFetchingNextPage = true;
      }

      const isTag = !['games', 'accounts', 'groups', 'product_guides', 'product_coaching', 'product_events'].includes(
        selectedKey
      );

      // Search params for tags and collections are different than the others so we need to handle them separately
      const params = !isTag ? indices[selectedKey]?.params : { hitsPerPage: 18 };

      // Get the search index name based on the selected key (tab) and fallback to tags
      const index = !isTag ? selectedKey : 'unified_tags';

      // Set game filter if current game is set
      const filter = [indices[selectedKey]?.params?.filter].filter(Boolean);
      if (currentGame) {
        filter.push(indexFilterMap[selectedKey]);
      }
      // Do the actual search
      const searchData = await searchIndex(index, query, { ...params, page: page + 1, filter: filter.join(' AND ') });

      // Set results to the search data if it's the first page or append the hits to the existing results
      results =
        page > 0
          ? {
              ...searchData,
              indexName: index,
              hits: [...(results?.hits || []), ...(searchData?.hits || [])]
            }
          : searchData;

      // Set pagination variables
      hasNextPage = searchData && searchData?.totalPages > searchData?.page;
      nextPage = hasNextPage ? searchData?.page : nextPage;
    } catch (error) {
      // ...
    } finally {
      isFetching = false;
      isFetchingNextPage = false;
    }
  }

  function loadNextPage() {
    if (hasNextPage && !isFetching) {
      doSearch(nextPage);
    }
  }

  $: noResults = !isFetching && results.totalHits === 0;
  $: currentGame, query, selectedKey && doSearch();

  onMount(() => {
    doSearch();
  });
</script>

<div
  class="bg-ondark-low md:bg-ondark-low md:border-ondark-subtle mx-auto flex h-full w-full flex-col rounded-lg pt-4 md:overflow-y-scroll md:border md:p-6"
>
  <div class="mb-4 flex w-full items-center gap-4">
    <IconButton icon={faChevronLeft} variant="secondary" size="sm" on:click={() => dispatch('back')} />
    <h2 class="grow-1 text-body-md md:text-body-lg truncate text-white">
      {currentGame?.localizedShortTitles?.en || currentGame?.localizedTitles?.en || currentGame?.name || 'All'}
      {titleIndex[selectedKey] || selectedKey}
    </h2>
  </div>
  {#if noResults}
    <EmptyState
      kind="large"
      illustration="{STATIC_URL}/illustrations/thisisfine.svg"
      showBg={false}
      padding="p-2"
      imageSize="max-h-40"
    >
      <svelte:fragment slot="content">
        <p class="text-body-2xs text-balance">
          <span>Oops! We didn't find anything with</span>
          <span class="text-ondark-primary">"{query}"</span>
        </p>
      </svelte:fragment>
    </EmptyState>
  {:else}
    <div
      class="result-item flex shrink grow-0 flex-col gap-10 overflow-y-auto md:-mx-3 md:max-h-[calc(90svh-220px)] md:min-h-[400px] md:px-3"
    >
      {#if !isFetching}
        <div class="animate-drop-in-slow relative grid grid-cols-12 gap-x-2 gap-y-6 pt-3 md:gap-x-4">
          {#each results.hits as result (result.id || result.objectID)}
            {#if selectedKey === 'games'}
              <div class="rs:col-span-4 col-span-6 p-2 sm:col-span-3 sm:p-1 md:p-0" transition:fade|local>
                <GameCard rounded="rounded-2xl" showTitle game={result} truncate="" useAspectRatio />
              </div>
            {:else if selectedKey === 'accounts'}
              <div
                transition:fade|local
                class="rs:col-span-4 col-span-6 {results.hits.length > 6
                  ? 'md:col-span-3'
                  : 'md:col-span-4'} px-1 md:px-0"
              >
                <ExpertCardSquare
                  partnerType={result.is_pro ? MetafyPartnerEnum.Pro : result.is_core ? MetafyPartnerEnum.Core : null}
                  showSummary
                  showBio={!result.summary}
                  expert={result}
                  {currentGame}
                  hideAvailability
                />
              </div>
            {:else if selectedKey === 'groups'}
              <div
                transition:fade|local
                class="rs:col-span-6 rs:px-0 rs:overflow-visible col-span-12 overflow-hidden px-8 md:col-span-4"
              >
                <GroupCard group={result} />
              </div>
            {:else if selectedKey === 'product_guides'}
              <div
                transition:fade|local
                class="rs:col-span-6 rs:px-0 rs:overflow-visible col-span-12 overflow-hidden px-8 md:col-span-4"
              >
                <GuideCard guide={result} />
              </div>
            {:else if selectedKey === 'product_coaching'}
              <div
                transition:fade|local
                class="rs:col-span-6 rs:px-0 rs:overflow-visible col-span-12 overflow-hidden px-8 lg:col-span-4"
              >
                <SessionCard session={result} />
              </div>
            {:else if selectedKey === 'product_events'}
              <div
                transition:fade|local
                class="rs:col-span-6 rs:px-0 rs:overflow-visible col-span-12 overflow-hidden px-8 lg:col-span-4"
              >
                <EventCard event={result} />
              </div>
            {/if}
          {/each}
        </div>
        {#if hasNextPage}
          <Button
            variant="secondary"
            width="full"
            on:click={loadNextPage}
            class="shrink-0"
            loading={isFetchingNextPage}
          >
            Show more
          </Button>
        {/if}
      {:else}
        <div class="spinner my-4 h-full w-full" />
      {/if}
    </div>
  {/if}
</div>

<style lang="postcss">
  .result-item :global(.highlight em) {
    @apply text-action-ondark-base not-italic;
  }
  .result-item :global(.highlight.text-ondark-subtle em) {
    @apply text-action-ondark-active font-semibold not-italic;
  }
</style>
